import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/CreateProfilePage.css';
import ValidationNotification from '../components/ValidationNotification';
import { signUpUser, isValidEmail, isValidPassword, isValidName } from '../services/userService';

const CreateProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fornavn: '',
    efternavn: '',
    email: '',
    password: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [notification, setNotification] = useState({
    message: '',
    isVisible: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const showNotification = (message: string) => {
    setNotification({
      message,
      isVisible: true
    });
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      hideNotification();
    }, 5000);
  };

  const hideNotification = () => {
    setNotification({
      ...notification,
      isVisible: false
    });
  };

  const validateForm = (): boolean => {
    // Check required fields
    if (!formData.fornavn || !formData.email || !formData.password) {
      showNotification("Fornavn, Email og Password er påkrævede felter");
      return false;
    }

    // Email validation
    if (!isValidEmail(formData.email)) {
      showNotification("Skriv venligst en valid e-mail");
      return false;
    }

    // Password validation
    if (!isValidPassword(formData.password)) {
      showNotification("Dit password skal indeholde Store og små bogstaver og min. være 8 karakterer");
      return false;
    }

    // Name validation for first name
    if (!isValidName(formData.fornavn)) {
      showNotification("Dit navn skal kun indeholde bogstaver");
      return false;
    }

    // Name validation for last name (if provided)
    if (formData.efternavn && !isValidName(formData.efternavn)) {
      showNotification("Dit navn skal kun indeholde bogstaver");
      return false;
    }

    // Terms and conditions validation
    if (!termsAccepted) {
      showNotification("Du skal acceptere betingelserne for at fortsætte");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Call the sign up service
      await signUpUser(
        formData.email,
        formData.password,
        formData.fornavn,
        formData.efternavn
      );
      
      // Navigate to onboarding step 1 after successful submission
      navigate('/onboarding/step-1');
    } catch (error: any) {
      console.error('Error during sign up:', error);
      showNotification(error.message || 'Der opstod en fejl under oprettelse af profil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-profile-page">
      {/* Background Image */}
      <div className="background-image"></div>

      {/* Validation Notification */}
      <ValidationNotification
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />

      {/* Logo */}
      <img src="/images/logo.png" alt="Wiin Logo" className="w-logo" />

      {/* Welcome Text */}
      <h1 className="welcome-text">Bliv medlem af Wiin</h1>
      <p className="subheading">Opret profil</p>

      {/* Sign-up Form */}
      <form className="profile-form auth-container" onSubmit={handleSubmit}>
        <div className="form-field">
          <input
            type="text"
            name="fornavn"
            placeholder="Fornavn"
            value={formData.fornavn}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-field">
          <input
            type="text"
            name="efternavn"
            placeholder="Efternavn"
            value={formData.efternavn}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-field">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-field">
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z" fill="#9E9E9E"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5ZM12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7ZM12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15Z" fill="#9E9E9E"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Terms and Conditions Checkbox */}
        <div className="terms-container">
          <label className="terms-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="terms-checkbox"
            />
            <span className="terms-text">Jeg accepterer betingelser her</span>
          </label>
        </div>

        {/* Primary Button */}
        <button 
          type="submit" 
          className="primary-button"
          disabled={loading}
        >
          <span>{loading ? 'Opretter profil...' : 'Opret profil'}</span>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.67 9.07L23.67 13.35L18.67 17.63"
              stroke="#FFFFFF"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2.09 13.35H23.67"
              stroke="#FFFFFF"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>

      {/* Footer Text */}
      <p className="footer-text">
        Har du allerede en <span className="highlight-text" onClick={() => navigate('/login')}>profil</span>?
      </p>
    </div>
  );
};

export default CreateProfilePage; 